/**
 * 列偏好编辑混入 (Mixin)
 *
 * 提供表格列编辑的完整功能：显隐切换、拖拽排序、列宽管理、本地缓存 + 后端持久化。
 * 支持双端独立存储：桌面端 pageType / 手机端 pageType_m。
 *
 * 使用页面需提供：
 *   1. data 中定义 pageType (String)                —— 页面标识，如 'productProgress'
 *   2. data 中定义 mobileColumnFilter (Array)       —— 手机端可用列 prop 数组，如 ['serialCode','name',...]
 *   3. computed 中定义 baseColumnDefs ()             —— 返回列定义数组 [{prop, label, ...}]
 *
 * 模板中使用：
 *   <card-table
 *     :tableColumn="effectiveVisibleColumns"
 *     :mobileColumns="mobileColumnFilter"
 *     :key="tableRenderKey"
 *     @column-order-change="onColumnOrderChange"
 *     @column-width-change="onColumnWidthChange"
 *     ...
 *   />
 *
 *   <column-edit-dialog
 *     :visible.sync="columnEditVisible"
 *     :columns="tempOrderedColumns"
 *     :defaultColumns="effectiveDefaultColumnDefs"
 *     :title="columnEditTitle"
 *     :dialogWidth="columnEditDialogWidth"
 *     :showWidth="true"
 *     @confirm="handleColumnSave"
 *     @cancel="handleColumnCancel"
 *   />
 */

import { UpdateProgressConfig } from '@/api/mainapi.js';
import UserInfoUtils from '@/utils/userInfoUtils.js';

export default {
  data() {
    return {
      // ============== 弹窗状态 ==============
      columnEditVisible: false,
      tempOrderedColumns: [],       // 弹窗中临时编辑的列列表 [{prop, label, visible, width}]
      tableRenderKey: 0,            // 强制 card-table 重建，确保列顺序/宽度生效
      editingMobileColumns: false,  // 当前编辑的是手机端(true)还是桌面端(false)

      // ============== 桌面端列偏好 ==============
      checkedColumns: [],           // 实际生效的列 prop 列表
      columnOrderMap: [],           // 按用户偏好的列 prop 顺序
      columnWidthMap: {},           // 桌面端列宽映射 { prop: widthNumber }
      columnLoaded: false,          // 是否已加载桌面端偏好

      // ============== 手机端列偏好 ==============
      mobileCheckedColumns: [],     // 手机端可见列 prop 列表
      mobileColumnOrderMap: [],     // 手机端列 prop 顺序
      mobileColumnWidthMap: {},     // 手机端列宽映射 { prop: widthNumber }
      mobileColumnLoaded: false,    // 是否已加载手机端偏好

      // ============== 手机端可用列过滤（页面 data 中覆盖）==============
      mobileColumnFilter: [],

      // ============== 手机端编辑弹窗中展示的列范围（默认同 mobileColumnFilter）==============
      mobileColumnEditFilter: []
    };
  },

  created() {
    // ★ 必须在 created 中初始化默认列，确保子组件 card-table mount 时 tableColumn 非空
    // 避免初始化时序问题导致列类型（如 link、tag 等自定义列）在触屏等设备上丢失
    this._setDefaultColumns();
    this._setDefaultMobileColumns();
  },

  computed: {
    // ============== 设备检测 ==============

    /**
     * 是否手机端（window.innerWidth <= 768）
     */
    isMobile() {
      return window.innerWidth <= 768;
    },

    /**
     * 列编辑弹窗标题（根据当前编辑端自动切换）
     */
    columnEditTitle() {
      return this.editingMobileColumns
        ? '编辑表格列（手机端 — 拖拽排序 / 勾选显隐 / 列宽）'
        : '编辑表格列（电脑端 — 拖拽排序 / 勾选显隐 / 列宽）';
    },

    /**
     * 列编辑弹窗宽度（手机端 300px，电脑端 520px）
     */
    columnEditDialogWidth() {
      return this.editingMobileColumns ? '300px' : '520px';
    },

    // ============== 桌面端列 ==============

    /**
     * 按桌面端用户偏好顺序排列的列定义（含宽度注入）
     */
    orderedColumns() {
      const defs = this.baseColumnDefs || [];
      const map = new Map(defs.map(c => [c.prop, c]));
      const ordered = [];
      for (const prop of this.columnOrderMap) {
        const col = map.get(prop);
        if (col) ordered.push(col);
      }
      for (const col of defs) {
        if (!this.columnOrderMap.includes(col.prop)) {
          ordered.push(col);
        }
      }
      return ordered;
    },

    /**
     * 桌面端可见列（含宽度注入，传给 card-table 的 :tableColumn 用）
     */
    visibleColumns() {
      return this.orderedColumns
        .filter(col => this.checkedColumns.includes(col.prop))
        .map(col => this._injectWidth(col, this.columnWidthMap));
    },

    // ============== 手机端列 ==============

    /**
     * 按手机端用户偏好顺序排列的列定义
     */
    mobileOrderedColumns() {
      const defs = this.baseColumnDefs || [];
      // mobileColumnEditFilter 控制编辑弹窗可编辑列范围，默认回退到 mobileColumnFilter
      const filter = (this.mobileColumnEditFilter && this.mobileColumnEditFilter.length)
        ? this.mobileColumnEditFilter
        : this.mobileColumnFilter;
      const map = new Map(defs.map(c => [c.prop, c]));
      const ordered = [];
      for (const prop of this.mobileColumnOrderMap) {
        const col = map.get(prop);
        if (col && filter.includes(prop)) ordered.push(col);
      }
      for (const prop of filter) {
        if (!this.mobileColumnOrderMap.includes(prop)) {
          const col = map.get(prop);
          if (col) ordered.push(col);
        }
      }
      return ordered;
    },

    /**
     * 手机端可见列（含宽度注入）
     */
    mobileVisibleColumns() {
      return this.mobileOrderedColumns
        .filter(col => this.mobileCheckedColumns.includes(col.prop))
        .map(col => this._injectWidth(col, this.mobileColumnWidthMap));
    },

    // ============== 自适应（当前设备） ==============

    /**
     * 当前设备可见列（模板中统一用这个传给 card-table）
     */
    effectiveVisibleColumns() {
      return this.isMobile ? this.mobileVisibleColumns : this.visibleColumns;
    },

    /**
     * 当前设备生效的列宽映射（方便透传给 card-table 做宽度初始化）
     */
    effectiveColumnWidthMap() {
      return this.isMobile ? this.mobileColumnWidthMap : this.columnWidthMap;
    },

    /**
     * 恢复默认时的列定义（手机端编辑时仅保留手机可用列）
     */
    effectiveDefaultColumnDefs() {
      if (this.editingMobileColumns) {
        const filter = this.mobileColumnFilter;
        return (this.baseColumnDefs || []).filter(c => filter.includes(c.prop));
      }
      return this.baseColumnDefs;
    }
  },

  methods: {
    // ==================== 列编辑弹窗 ====================

    /**
     * 打开列编辑弹窗（自动判断电脑端/手机端）
     */
    openColumnEdit() {
      const isOnMobile = window.innerWidth <= 768;
      this.editingMobileColumns = isOnMobile;

      const widthMap = isOnMobile ? this.mobileColumnWidthMap : this.columnWidthMap;
      const checked = isOnMobile ? this.mobileCheckedColumns : this.checkedColumns;
      const ordered = isOnMobile ? this.mobileOrderedColumns : this.orderedColumns;

      this.tempOrderedColumns = ordered.map(col => ({
        prop: col.prop,
        label: col.label,
        visible: checked.includes(col.prop),
        width: widthMap[col.prop] || undefined,
        autoWidth: widthMap[col.prop] ? undefined : this._getColumnDefaultWidth(col.prop)
      }));
      this.columnEditVisible = true;
    },

    /** 取消列编辑：丢弃临时修改 */
    handleColumnCancel() {
      this.columnEditVisible = false;
    },

    /** 确定列编辑：将临时修改同步到对应端的列偏好 */
    handleColumnSave(columns) {
      // 兼容旧版无参调用：从 tempOrderedColumns 取
      const cols = columns || this.tempOrderedColumns;
      const visibleProps = cols.filter(c => c.visible).map(c => c.prop);
      if (visibleProps.length === 0) {
        this.$message.warning('至少保留一列');
        return;
      }

      if (this.editingMobileColumns) {
        this.mobileColumnOrderMap = cols.map(c => c.prop);
        this.mobileCheckedColumns = visibleProps;
        // 更新手机端宽度
        cols.forEach(c => {
          if (c.width && c.width > 0) {
            this.$set(this.mobileColumnWidthMap, c.prop, c.width);
          } else {
            this.$delete(this.mobileColumnWidthMap, c.prop);
          }
        });
      } else {
        this.columnOrderMap = cols.map(c => c.prop);
        this.checkedColumns = visibleProps;
        // 更新桌面端宽度
        cols.forEach(c => {
          if (c.width && c.width > 0) {
            this.$set(this.columnWidthMap, c.prop, c.width);
          } else {
            this.$delete(this.columnWidthMap, c.prop);
          }
        });
      }

      this.columnEditVisible = false;
      this.tableRenderKey++;
      this.saveColumnPref();
      this.$message.success('列设置已保存');
    },

    // ==================== 表格直接拖拽 ====================

    /**
     * 表格列拖拽排序变化（card-table 的 @column-order-change 事件）
     */
    onColumnOrderChange(newColumns) {
      if (this.isMobile) {
        const filter = this.mobileColumnFilter;
        this.mobileColumnOrderMap = newColumns
          .filter(c => filter.includes(c.prop))
          .map(c => c.prop);
      } else {
        this.columnOrderMap = newColumns.map(c => c.prop);
      }
      this.saveColumnPref();
    },

    // ==================== 列宽变更 ====================

    /**
     * 列宽拖拽变化（card-table 的 @column-width-change 事件）
     * @param {{ prop: string, width: number }} payload
     */
    onColumnWidthChange(payload) {
      if (!payload || !payload.prop) return;
      const { prop, width } = payload;
      if (this.isMobile) {
        if (width && width > 0) {
          this.$set(this.mobileColumnWidthMap, prop, width);
        } else {
          this.$delete(this.mobileColumnWidthMap, prop);
        }
      } else {
        if (width && width > 0) {
          this.$set(this.columnWidthMap, prop, width);
        } else {
          this.$delete(this.columnWidthMap, prop);
        }
      }
      this.saveColumnPref();
    },

    // ==================== 持久化 ====================

    /**
     * 保存列偏好到本地缓存 + 后端（双端同时保存）
     * 格式: [{prop, visible, width?}]
     */
    async saveColumnPref() {
      const userCode = UserInfoUtils.getUserCode();
      if (!userCode) return;

      const promises = [];

      // 桌面端偏好
      if (this.columnLoaded) {
        const pcColumns = this.orderedColumns.map(col => {
          const entry = {
            prop: col.prop,
            visible: this.checkedColumns.includes(col.prop)
          };
          const w = this.columnWidthMap[col.prop];
          if (w && w > 0) entry.width = w;
          return entry;
        });
        UserInfoUtils.setColumnPreference(this.pageType, pcColumns);
        promises.push(
          UpdateProgressConfig({ userCode, pageType: this.pageType, columns: pcColumns })
            .catch(e => console.warn('[columnEditMixin] 保存桌面端列偏好到后端失败', e))
        );
      }

      // 手机端偏好
      if (this.mobileColumnLoaded && this.mobileColumnFilter.length > 0) {
        const mbColumns = this.mobileOrderedColumns.map(col => {
          const entry = {
            prop: col.prop,
            visible: this.mobileCheckedColumns.includes(col.prop)
          };
          const w = this.mobileColumnWidthMap[col.prop];
          if (w && w > 0) entry.width = w;
          return entry;
        });
        UserInfoUtils.setColumnPreference(this.pageType + '_m', mbColumns);
        promises.push(
          UpdateProgressConfig({ userCode, pageType: this.pageType + '_m', columns: mbColumns })
            .catch(e => console.warn('[columnEditMixin] 保存手机端列偏好到后端失败', e))
        );
      }

      await Promise.all(promises);
    },

    /**
     * 从本地缓存加载列偏好（同时加载桌面端和手机端）
     * 页面 mounted 中需调用：await this.loadColumnPref()
     */
    async loadColumnPref() {
      const userCode = UserInfoUtils.getUserCode();
      if (!userCode) {
        this._setDefaultColumns();
        this._setDefaultMobileColumns();
        this.columnLoaded = true;
        this.mobileColumnLoaded = true;
        return;
      }
      try {
        // --- 加载桌面端偏好 ---
        const pref = UserInfoUtils.getColumnPreference(this.pageType);
        const pcArray = Array.isArray(pref) ? pref : (pref && pref.columns ? pref.columns : null);
        if (pcArray && pcArray.length) {
          this.columnOrderMap = pcArray.map(c => c.prop);
          this.checkedColumns = pcArray.filter(c => c.visible).map(c => c.prop);
          this.columnWidthMap = {};
          pcArray.forEach(c => {
            if (c.width && c.width > 0) this.$set(this.columnWidthMap, c.prop, c.width);
          });
          // ✅ 兼容老用户的缓存偏好：代码里后来新增的列自动补上并默认显示
          this._mergeNewColumns(pcArray.map(c => c.prop), false);
        } else {
          this._setDefaultColumns();
        }

        // --- 加载手机端偏好 ---
        const mobilePref = UserInfoUtils.getColumnPreference(this.pageType + '_m');
        const mbArray = Array.isArray(mobilePref) ? mobilePref : (mobilePref && mobilePref.columns ? mobilePref.columns : null);
        if (mbArray && mbArray.length) {
          this.mobileColumnOrderMap = mbArray.map(c => c.prop);
          this.mobileCheckedColumns = mbArray.filter(c => c.visible).map(c => c.prop);
          this.mobileColumnWidthMap = {};
          mbArray.forEach(c => {
            if (c.width && c.width > 0) this.$set(this.mobileColumnWidthMap, c.prop, c.width);
          });
          // ✅ 同理兼容手机端缓存偏好
          this._mergeNewColumns(mbArray.map(c => c.prop), true);
        } else {
          this._setDefaultMobileColumns();
        }
      } catch (e) {
        console.warn('[columnEditMixin] 加载列偏好失败，使用默认', e);
        this._setDefaultColumns();
        this._setDefaultMobileColumns();
      }
      this.columnLoaded = true;
      this.mobileColumnLoaded = true;
    },

    // ==================== 内部方法 ====================

    /** 从 baseColumnDefs 获取列的默认宽度（width 或 minWidth） */
    _getColumnDefaultWidth(prop) {
      const defs = this.baseColumnDefs || [];
      const col = defs.find(c => c.prop === prop);
      if (col) {
        return col.width || col.minWidth || undefined;
      }
      return undefined;
    },

    /** 给列对象注入宽度（不修改原对象） */
    _injectWidth(col, widthMap) {
      const w = widthMap[col.prop];
      if (w && w > 0) {
        const result = { ...col, width: w };
        // 自定义列（progress、dateEdit、textEdit）读取 col.column.width
        if (col.column && typeof col.column === 'object') {
          result.column = { ...col.column, width: w };
        }
        return result;
      }
      return col;
    },

    /**
     * 把「缓存偏好里还没有的新列」按 baseColumnDefs 的默认顺序插进用户的列顺序中
     * （插在默认顺序里它前面那列的后面，尽量贴近设计位置，同时不打乱用户已有的相对顺序）
     * @param {string[]} orderList 用户已保存的列顺序
     * @param {string[]} newcomers 新增列的 prop 数组
     * @returns {string[]} 插入后的列顺序
     */
    _insertByDefaultOrder(orderList, newcomers) {
      const defs = this.baseColumnDefs || [];
      const result = [...orderList];
      newcomers.forEach(prop => {
        const defIndex = defs.findIndex(d => d.prop === prop);
        let insertAt = result.length;
        for (let i = defIndex - 1; i >= 0; i--) {
          const prevIndex = result.indexOf(defs[i].prop);
          if (prevIndex > -1) {
            insertAt = prevIndex + 1;
            break;
          }
        }
        result.splice(insertAt, 0, prop);
      });
      return result;
    },

    /**
     * ✅ 兼容历史列偏好：缓存中没记录的列（代码里后来新增的列）自动补进顺序并默认显示，
     * 解决「老用户升级后看不到新加列」的问题。
     * 注意：用户在列设置里主动取消勾选的列会被缓存记录（visible:false），不会走到这里，不会被强制显示。
     * @param {string[]} savedProps 缓存偏好中已记录的列 prop
     * @param {boolean} isMobilePref 是否为手机端偏好
     */
    _mergeNewColumns(savedProps, isMobilePref) {
      const defs = this.baseColumnDefs || [];
      const defProps = defs.map(c => c.prop);

      if (isMobilePref) {
        // 手机端可编辑范围（决定顺序），默认可见范围（决定是否自动勾选）
        const editScope = (this.mobileColumnEditFilter && this.mobileColumnEditFilter.length)
          ? this.mobileColumnEditFilter
          : this.mobileColumnFilter;
        const visibleScope = this.mobileColumnFilter || [];
        const newcomers = editScope.filter(prop => defProps.includes(prop) && !savedProps.includes(prop));
        if (!newcomers.length) return;

        this.mobileColumnOrderMap = this._insertByDefaultOrder(this.mobileColumnOrderMap, newcomers);
        // 只有原本就属于手机端默认显示范围的列才自动勾选，避免改变手机端默认排版
        const toCheck = newcomers.filter(prop => visibleScope.includes(prop));
        if (toCheck.length) {
          this.mobileCheckedColumns = [...this.mobileCheckedColumns, ...toCheck];
        }
      } else {
        const newcomers = defProps.filter(prop => !savedProps.includes(prop));
        if (!newcomers.length) return;

        this.columnOrderMap = this._insertByDefaultOrder(this.columnOrderMap, newcomers);
        this.checkedColumns = [...this.checkedColumns, ...newcomers];
      }
    },

    /** 重置桌面端为默认：全部显示、原始顺序、清空自定义宽度 */
    _setDefaultColumns() {
      const defs = this.baseColumnDefs || [];
      this.columnOrderMap = defs.map(c => c.prop);
      this.checkedColumns = defs.map(c => c.prop);
      this.columnWidthMap = {};
    },

    /** 重置手机端为默认：mobileColumnFilter 中全部显示、按 baseColumnDefs 顺序排列、清空自定义宽度 */
    _setDefaultMobileColumns() {
      const filter = this.mobileColumnFilter;
      if (!filter || !filter.length) {
        this.mobileColumnOrderMap = [];
        this.mobileCheckedColumns = [];
        this.mobileColumnWidthMap = {};
        return;
      }
      const defs = this.baseColumnDefs || [];
      const ordered = [];
      for (const col of defs) {
        if (filter.includes(col.prop)) {
          ordered.push(col.prop);
        }
      }
      this.mobileColumnOrderMap = ordered;
      this.mobileCheckedColumns = [...ordered];
      this.mobileColumnWidthMap = {};
    }
  }
};
